// useMapPick — Klick-Uebernahme-Logik fuer den PDF-Arbeitsplatz des Mapping-Editors: verwaltet
// das aktive Pick-Ziel ({ field?, kind }) und uebernimmt angeklickte Woerter je nach Ziel-Art
// (x-Band aus der Wort-Box +/- band_pad ODER Text-Muster-Vorschlag, escaped). Ausgelagert aus
// AbMappingEditor.js (500-LOC-Regel) — Vorbild MappingView.tsx applyRef/pickBtn, angepasst an
// AbPdfViewers Wort-Klick-Interaktion (s. Kopfkommentar AbMappingEditor.js).

import { useEffect, useRef, useState } from 'react';
import { bandFromWord, escapeRegex } from './wordHighlight';

export default function useMapPick({ t, form, setForm, setField }) {
  const [pick, setPick] = useState(null); // { field?, kind } | null
  const pickTextRef = useRef('');

  // Esc beendet einen aktiven Pick (Konvention wie AbDocumentDetail-Klick-Uebernahme-Modus).
  useEffect(() => {
    if (!pick) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPick(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pick]);

  const startPick = (target) => {
    setPick((prev) => (prev && prev.kind === target.kind && prev.field === target.field ? null : target));
    pickTextRef.current = '';
  };

  const applyPick = (target, word) => {
    if (!target) return;
    const bandPad = form.bandPad || 0;
    if (target.kind === 'row-anchor') {
      const { xMin, xMax } = bandFromWord(word, bandPad);
      setForm((p) => ({ ...p, page: word.page, rowAnchorXMin: xMin, rowAnchorXMax: xMax }));
      setPick(null);
      return;
    }
    if (target.kind === 'row-end') {
      setForm((p) => ({ ...p, rowEnd: (word.t || '').trim() || p.rowEnd }));
      setPick(null);
      return;
    }
    if (!target.field) { setPick(null); return; }
    if (target.kind === 'header-box') {
      setField(target.field, { box: { page: word.page, x0: word.x0, y0: word.y0, x1: word.x1, y1: word.y1 } });
      setPick(null);
      return;
    }
    if (target.kind === 'line-column') {
      const { xMin, xMax } = bandFromWord(word, bandPad);
      setForm((p) => ({ ...p, page: word.page }));
      setField(target.field, { xMin, xMax });
      setPick(null);
      return;
    }
    if (target.kind === 'line-unit') {
      const { xMin, xMax } = bandFromWord(word, bandPad);
      setField(target.field, { unitXMin: xMin, unitXMax: xMax });
      setPick(null);
      return;
    }
    // Text-Muster-Picks: erster Klick ersetzt, weitere haengen mit Leerzeichen an (bleibt aktiv).
    // header-presence bleibt Klartext (kein Regex-Feld), alle anderen werden escaped uebernommen.
    pickTextRef.current = pickTextRef.current ? `${pickTextRef.current} ${word.t}` : (word.t || '');
    if (target.kind === 'header-regex') setField(target.field, { regexPattern: escapeRegex(pickTextRef.current) });
    else if (target.kind === 'header-presence') setField(target.field, { presenceValue: pickTextRef.current });
    else if (target.kind === 'line-pattern') setField(target.field, { linePattern: escapeRegex(pickTextRef.current) });
    else if (target.kind === 'block-ppu-pattern') setField(target.field, { ppuPattern: escapeRegex(pickTextRef.current) });
  };

  const pickTargetLabel = (p) => {
    if (!p) return '';
    if (p.kind === 'row-anchor') return t('ab.map.pickTarget.rowAnchor');
    if (p.kind === 'row-end') return t('ab.map.pickTarget.rowEnd');
    if (p.kind === 'header-box') return t('ab.map.pickTarget.headerBox', { field: p.field });
    if (p.kind === 'header-regex') return t('ab.map.pickTarget.headerRegex', { field: p.field });
    if (p.kind === 'header-presence') return t('ab.map.pickTarget.headerPresence', { field: p.field });
    if (p.kind === 'line-column') return t('ab.map.pickTarget.column', { field: p.field });
    if (p.kind === 'line-unit') return t('ab.map.pickTarget.unit', { field: p.field });
    return t('ab.map.pickTarget.pattern', { field: p.field });
  };

  return { pick, setPick, startPick, applyPick, pickTargetLabel };
}
